import React, { createContext, useContext, useEffect, useState, useMemo } from "react";
import { User, Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import LoadingFallback from "@/components/LoadingFallback";
import { useSecurityMonitoring } from "@/hooks/use-security-monitoring";
import { useQueryClient } from "@tanstack/react-query";

interface AuthContextType {
  user: User | null;
  session: Session | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<{ error?: string; emailNotConfirmed?: boolean }>;
  signUp: (email: string, password: string, name?: string, orgData?: { name?: string; id?: string }) => Promise<{ error?: string }>;
  logout: () => Promise<void>;
  resetPassword: (email: string) => Promise<{ error?: string }>;
  updatePassword: (newPassword: string) => Promise<{ error?: string }>;
  loginAsTestUser: () => Promise<{ error?: string }>;
  resendVerificationEmail: (email: string) => Promise<{ error?: string }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const queryClient = useQueryClient();
  
  // Initialize security monitoring with current user
  const securityMonitoring = useSecurityMonitoring(user);

  // Invalidate role cache when user changes
  useEffect(() => {
    if (!user?.id) return;
    
    console.log('[AuthContext] User changed, invalidating role cache for:', user.id);
    queryClient.invalidateQueries({ queryKey: ['user-role', user.id] });
  }, [user?.id, queryClient]);

  useEffect(() => {
    let mounted = true;
    let authSubscription: any = null;

    const initializeAuth = async () => {
      try {
        // Set up auth state listener FIRST
        const { data: { subscription } } = supabase.auth.onAuthStateChange(
          async (event, session) => {
            if (!mounted) return;

            if (session) {
              // Wait a moment for JWT context to be established
              setTimeout(() => {
                if (mounted) {
                  setSession(session);
                  setUser(session.user);
                  setIsLoading(false);
                }
              }, 100);
            } else {
              setSession(null);
              setUser(null);
              setIsLoading(false);
            }
          }
        );

        authSubscription = subscription;

        // THEN check for existing session
        const { data: { session }, error } = await supabase.auth.getSession();
        
        if (mounted) {
          if (session) {
            // Wait for JWT context to be established
            setTimeout(() => {
              if (mounted) {
                setSession(session);
                setUser(session.user);
                setIsLoading(false);
              }
            }, 100);
          } else {
            setSession(null);
            setUser(null);
            setIsLoading(false);
          }
        }
      } catch (error) {
        if (mounted) {
          setSession(null);
          setUser(null);
          setIsLoading(false);
        }
      }
    };

    initializeAuth();

    return () => {
      mounted = false;
      if (authSubscription) {
        authSubscription.unsubscribe();
      }
    };
  }, []);

  const login = async (email: string, password: string) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      
      if (error) {
        // Log failed login attempt
        setTimeout(() => {
          securityMonitoring?.logLoginAttempt(false, email);
        }, 0);
        
        // Check if error is specifically about email not confirmed
        if (error.message.includes('Email not confirmed') || error.message.includes('email_not_confirmed')) {
          return { error: error.message, emailNotConfirmed: true };
        }
        
        return { error: error.message };
      }

      if (data.session) {
        // Wait for JWT context to be established before proceeding
        await new Promise(resolve => setTimeout(resolve, 200));
        
        // Verify the session is working with a test query
        try {
          const { error: testError } = await supabase.from('profiles').select('id').limit(1);
          if (testError && testError.message.includes('JWT')) {
            await new Promise(resolve => setTimeout(resolve, 500));
          }
        } catch (e) {
          // Session test failed, but proceeding
        }
        
        // Log successful login
        setTimeout(() => {
          securityMonitoring?.logLoginAttempt(true, email);
        }, 0);
      }
      
      return {};
    } catch (error: any) {
      // Log failed login attempt for unexpected errors
      setTimeout(() => {
        securityMonitoring?.logLoginAttempt(false, email);
      }, 0);
      return { error: error.message || 'Login failed' };
    }
  };

  const signUp = async (email: string, password: string, name?: string, orgData?: { name?: string; id?: string }) => {
    try {
      // Validate organization data if provided
      if (orgData) {
        // Validate organization name if creating
        if (orgData.name && orgData.name.trim().length < 2) {
          return { error: 'Organization name must be at least 2 characters long.' };
        }

        // Validate organization ID if joining
        if (orgData.id && !orgData.id.trim()) {
          return { error: 'Organization ID cannot be empty.' };
        }
      }

      // Redirect URL based on whether organization data is provided
      const redirectUrl = orgData 
        ? `${window.location.origin}/email-verified`
        : `${window.location.origin}/waiting-for-invitation`;
      
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: redirectUrl,
          data: {
            name: name || '',
            organization_data: orgData
          }
        }
      });

      if (error) {
        console.error('Signup error:', error);
        return { error: error.message };
      }

      // Record legal acceptance
      if (data.user) {
        try {
          await supabase.from('legal_acceptance').insert({
            user_id: data.user.id,
            terms_accepted: true,
            privacy_accepted: true,
            sla_accepted: true,
            document_version: 'v1.0'
          });
        } catch (acceptanceError) {
          console.error('Error recording legal acceptance:', acceptanceError);
        }
      }

      // If user is created immediately (email confirmation disabled), handle organization setup
      if (data.user && !data.user.email_confirmed_at) {
        // User needs to confirm email first, organization setup will happen in EmailVerified component
        return {};
      } else if (data.user && data.user.email_confirmed_at) {
        // Email confirmation is disabled, handle organization setup immediately
        return await handleOrganizationSetup(data.user.id, orgData);
      }

      return {};
    } catch (error: any) {
      console.error('Signup error:', error);
      return { error: error.message || 'An unexpected error occurred' };
    }
  };

  const handleOrganizationSetup = async (userId: string, orgData: { name?: string; id?: string }) => {
    try {
      if (orgData.name) {
        // Create new organization
        const { data, error } = await supabase.rpc('create_user_organization', {
          organization_name: orgData.name.trim()
        });

        if (error) throw error;

        const result = data as any;
        if (result?.error) {
          throw new Error(result.error);
        }

        // Log organization creation
        try {
          await securityMonitoring.logSecurityEvent(
            'admin_action_performed',
            { 
              action: 'organization_created',
              organization_name: orgData.name,
              organization_id: result.organization_id 
            },
            'low',
            'organization',
            result.organization_id
          );
        } catch (logError) {
          console.error('[Security] Failed to log organization creation:', logError);
        }

        return {};
      } else if (orgData.id) {
        // Join existing organization by updating profile
        const { error } = await supabase
          .from('profiles')
          .update({ organization_id: orgData.id.trim() })
          .eq('user_id', userId);

        if (error) {
          throw new Error('Invalid organization ID or you do not have permission to join this organization.');
        }

        // Log organization access
        try {
          await securityMonitoring.logSecurityEvent(
            'organization_access_attempted',
            { 
              action: 'user_joined_organization',
              organization_id: orgData.id,
              user_id: userId
            },
            'medium',
            'organization',
            orgData.id
          );
        } catch (logError) {
          console.error('[Security] Failed to log organization access:', logError);
        }

        return {};
      }

      return { error: 'Invalid organization data provided.' };
    } catch (error: any) {
      console.error('Organization setup error:', error);
      return { error: error.message || 'Failed to set up organization' };
    }
  };

  const logout = async () => {
    // Log logout event before clearing session
    if (user) {
      try {
        await securityMonitoring.logSecurityEvent(
          'logout',
          { 
            user_email: user.email,
            user_id: user.id 
          },
          'low'
        );
      } catch (logError) {
        console.error('[Security] Failed to log logout event:', logError);
      }
    }

    try {
      // Use global logout to ensure all sessions are terminated
      const { error } = await supabase.auth.signOut({ scope: 'global' });
      if (error) {
        // Continue with cleanup even if server logout fails
      }
    } catch (error) {
      // Continue with cleanup even if logout request fails
    }
    
    // Always clear local state and storage (critical for security)
    setUser(null);
    setSession(null);
    
    // Clear all Supabase-related localStorage items
    try {
      // Clear the correct storage key that matches client configuration
      localStorage.removeItem('supabase.auth.token');
      localStorage.removeItem('sb-crpxcsrljmdcrgnedfxb-auth-token');
      
      // Clear any other potential Supabase storage items
      Object.keys(localStorage).forEach(key => {
        if (key.startsWith('supabase') || key.startsWith('sb-')) {
          localStorage.removeItem(key);
        }
      });
    } catch (storageError) {
      // Storage cleanup error
    }
    
    // Force a session check to ensure we're truly logged out
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        // Additional cleanup if session persists
        await supabase.auth.signOut({ scope: 'global' });
      }
    } catch (sessionCheckError) {
      // Session validation error
    }
  };

  const resetPassword = async (email: string) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    
    // Log password reset request
    try {
      await securityMonitoring.logSecurityEvent(
        'password_reset_requested',
        { 
          email,
          success: !error 
        },
        'medium'
      );
    } catch (logError) {
      console.error('[Security] Failed to log password reset event:', logError);
    }
    
    if (error) {
      return { error: error.message };
    }
    
    return {};
  };

  const updatePassword = async (newPassword: string) => {
    const { error } = await supabase.auth.updateUser({
      password: newPassword
    });
    
    // Log password change
    if (user) {
      try {
        await securityMonitoring.logSecurityEvent(
          'password_changed',
          { 
            user_email: user.email,
            success: !error 
          },
          'high'
        );
      } catch (logError) {
        console.error('[Security] Failed to log password change event:', logError);
      }
    }
    
    if (error) {
      return { error: error.message };
    }
    
    return {};
  };

  const loginAsTestUser = async () => {
    try {
      const { data, error } = await supabase.functions.invoke('test-login', {
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (error) {
        console.error('Test login function error:', error);
        return { error: 'Failed to initialize test account' };
      }

      if (data.error) {
        console.error('Test login error:', data.error);
        return { error: data.error };
      }

      // Use the credentials to sign in
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: data.email,
        password: data.password,
      });

      if (signInError) {
        console.error('Test user signin error:', signInError);
        return { error: 'Failed to sign in to test account' };
      }

      return {};
    } catch (error: any) {
      console.error('Test login error:', error);
      return { error: 'Failed to access test account' };
    }
  };

  const resendVerificationEmail = async (email: string) => {
    try {
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email: email,
        options: {
          emailRedirectTo: `${window.location.origin}/auth`
        }
      });
      
      if (error) {
        return { error: error.message };
      }
      
      return {};
    } catch (error: any) {
      return { error: error.message || "Failed to resend verification email" };
    }
  };

  const value = useMemo(
    () => ({
      user,
      session,
      isAuthenticated: !!user,
      isLoading,
      login,
      signUp,
      logout,
      resetPassword,
      updatePassword,
      loginAsTestUser,
      resendVerificationEmail
    }),
    [user, session, isLoading]
  );

  if (isLoading) {
    return <LoadingFallback message="Initializing application..." />;
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};